import {
  createEntityAdapter,
  createSlice,
  PayloadAction,
  createSelector,
  prepareAutoBatched,
} from "@reduxjs/toolkit"
import { allFiltersCleared } from "./search-actions"
import { SelectorStateInterface, SliceThunkAction } from "./type-utils"

export type FilterConfig = { type: string; [key: string]: any }
type RegistrationEntity = {
  id: string
  config: FilterConfig
}
const registrationsAdapter = createEntityAdapter<RegistrationEntity>()

type ValuesEntity = {
  id: string
  values: any[]
}
const valuesAdapter = createEntityAdapter<ValuesEntity>()

const initialState = {
  registrations: registrationsAdapter.getInitialState(),
  values: valuesAdapter.getInitialState(),
}

type SliceState = typeof initialState

const registrationSelectors = registrationsAdapter.getSelectors<SliceState>(
  (state) => state.registrations,
)
const valuesSelectors = valuesAdapter.getSelectors<SliceState>(
  (state) => state.values,
)

export const filtersSlice = createSlice({
  name: "filters",
  initialState,

  reducers: {
    filterRegistered: {
      reducer(
        state,
        action: PayloadAction<{ id: string; config: FilterConfig }>,
      ) {
        const { id, config } = action.payload
        registrationsAdapter.addOne(state.registrations, { id, config })
      },
      prepare: prepareAutoBatched<{ id: string; config: FilterConfig }>(),
    },

    filterDeregistered(state, action: PayloadAction<string>) {
      const id = action.payload
      registrationsAdapter.removeOne(state.registrations, id)
      valuesAdapter.removeOne(state.values, id)
    },

    filterValuesInitialised: {
      reducer(state, action: PayloadAction<{ id: string; values: any[] }>) {
        const { id, values } = action.payload
        valuesAdapter.addOne(state.values, { id, values })
      },
      prepare: prepareAutoBatched<{ id: string; values: any[] }>(),
    },

    filterValuesSet(
      state,
      action: PayloadAction<{ id: string; values: any[] }>,
    ) {
      const { id, values } = action.payload
      valuesAdapter.setOne(state.values, { id, values })
    },
  },

  extraReducers: (builder) => {
    builder.addCase(allFiltersCleared, (state) => {
      valuesAdapter.removeAll(state.values)
    })
  },

  selectors: {
    selectQueryFilters: createSelector(
      [registrationSelectors.selectAll, valuesSelectors.selectEntities],
      (allRegistrations, valuesEntities) => {
        const entries = allRegistrations
          .filter(({ id }) => (valuesEntities[id]?.values ?? []).length > 0)
          .map(
            ({ id, config }) =>
              [
                id,
                { ...config, values: valuesEntities[id]?.values ?? [] },
              ] as const,
          )
        return Object.fromEntries(entries)
      },
    ),

    selectFilterValues: createSelector(
      [valuesSelectors.selectById],
      (entity) => entity?.values ?? [],
    ),

    selectFilterRegistered: createSelector(
      [registrationSelectors.selectById],
      (entity) => entity !== undefined,
    ),
  },
})

type FiltersThunkAction = SliceThunkAction<typeof filtersSlice>
function updateFilterValues(
  filterId: string,
  updater: (prev: any[]) => any[],
): FiltersThunkAction {
  return function (dispatch, getState) {
    const currentValues = filtersSlice.selectors.selectFilterValues(
      getState(),
      filterId,
    )

    dispatch(
      filtersSlice.actions.filterValuesSet({
        id: filterId,
        values: updater(currentValues),
      }),
    )
  }
}

const {
  filterRegistered,
  filterDeregistered,
  filterValuesSet: filterValuesSet,
  filterValuesInitialised,
} = filtersSlice.actions
export const filtersActions = {
  filterRegistered,
  filterDeregistered,
  filterValuesSet,
  filterValuesInitialised,
  updateFilterValues,
}

const { selectQueryFilters, selectFilterValues, selectFilterRegistered } =
  filtersSlice.selectors
type RootState = SelectorStateInterface<typeof filtersSlice.selectSlice>
const { selectIds: selectFilterIds } =
  registrationsAdapter.getSelectors<RootState>(
    (state) => state.filters.registrations,
  )
export const filtersSelectors = {
  selectQueryFilters,
  selectFilterRegistered,
  selectFilterValues,
  selectFilterIds,
}
